/**
 * 이메일 도메인 힌트 — 서버(app/auth/email_domain.py)와 동일한 규칙의 클라이언트 사본.
 *
 * 서버가 가입/변경 요청을 400 으로 막지만, 그건 '제출한 뒤'다. 여기서는 입력 중에
 * 바로 알려줘서 사용자가 제출 전에 고치게 한다(서버 검증이 최종 방어선).
 *
 * 실측 근거(2026-08-10, 발송함 반송 101통 전수):
 *   · hanmali.net(hanmail 오타) 1명 → 22통 반송, 상태코드 4.x.x 라 서버가 무한 재시도
 *   · hanmail/kakao 반송 16통 중 15통이 550 5.2.1 "Mailbox is inactive"(카카오 휴면 잠금)
 */

/** 국내·해외 주요 무료메일 — 오타 근접 판정의 기준점. */
export const MAJOR_DOMAINS = [
  'gmail.com', 'naver.com', 'hanmail.net', 'daum.net', 'kakao.com',
  'nate.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'yahoo.co.kr',
  'icloud.com', 'korea.com', 'empas.com', 'hanmir.com', 'dreamwiz.com',
]

/** 실측·통상 오타 → 정정 도메인. */
const TYPO_PAIRS: [string, string][] = [
  ['hanmali.net', 'hanmail.net'], ['hanmail.com', 'hanmail.net'],
  ['hamail.net', 'hanmail.net'], ['hanmial.net', 'hanmail.net'],
  ['hanmaill.net', 'hanmail.net'], ['hanmali.com', 'hanmail.net'],
  ['navr.com', 'naver.com'], ['nave.com', 'naver.com'], ['naver.co', 'naver.com'],
  ['naver.con', 'naver.com'], ['nvaer.com', 'naver.com'], ['naber.com', 'naver.com'],
  ['gmial.com', 'gmail.com'], ['gmai.com', 'gmail.com'], ['gmail.con', 'gmail.com'],
  ['gmaill.com', 'gmail.com'], ['gmail.co', 'gmail.com'], ['gmali.com', 'gmail.com'],
  ['gmail.cm', 'gmail.com'], ['gamil.com', 'gmail.com'],
  ['daum.com', 'daum.net'], ['dawm.net', 'daum.net'], ['daun.net', 'daum.net'],
  ['kakao.net', 'kakao.com'], ['kako.com', 'kakao.com'],
  ['nate.net', 'nate.com'], ['naet.com', 'nate.com'],
  ['hotmial.com', 'hotmail.com'], ['hotmail.co', 'hotmail.com'],
  ['outlok.com', 'outlook.com'], ['yaho.com', 'yahoo.com'], ['yahho.com', 'yahoo.com'],
]
export const TYPO_FIXES: Record<string, string> = Object.fromEntries(TYPO_PAIRS)

/**
 * 카카오(다음) 계열 — 장기 미접속 시 계정을 '휴면'으로 잠가 메일 수신을 차단한다.
 * 주소 자체는 유효하므로 막지 않고 안내만 한다.
 */
export const DORMANT_RISK_DOMAINS = ['hanmail.net', 'daum.net', 'kakao.com']

/** Optimal String Alignment 거리(전치 1회를 1로 셈). cap 초과 시 cap+1. */
function damerau(a: string, b: string, cap = 1): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1
  let prev2: number[] = []
  let prev: number[] = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const cur = new Array<number>(b.length + 1).fill(0)
    cur[0] = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cur[j] = Math.min(cur[j], prev2[j - 2] + cost)
      }
    }
    if (Math.min(...cur) > cap) return cap + 1
    prev2 = prev
    prev = cur
  }
  return prev[b.length]
}

export type EmailDomainHint = {
  domain: string
  /** 오타로 판단된 경우 제안 도메인(없으면 null) */
  suggestion: string | null
  /** 정정된 전체 주소(제안이 있을 때만) */
  suggestedEmail: string | null
  /** 카카오 계열 — 휴면 잠금 안내 대상 */
  dormantRisk: boolean
}

/** 입력 중인 이메일에 대한 힌트. 도메인이 아직 불완전하면 조용히 넘어간다. */
export function inspectEmailDomain(email: string): EmailDomainHint | null {
  const addr = (email || '').trim().toLowerCase()
  const at = addr.lastIndexOf('@')
  if (at <= 0) return null
  const local = addr.slice(0, at)
  const domain = addr.slice(at + 1)
  // 점 뒤 TLD 가 2자 이상 찍히기 전에는 판정하지 않는다(타이핑 중 깜빡임 방지).
  if (!/^[\w-]+(\.[\w-]+)+$/.test(domain) || !/\.[a-z]{2,}$/.test(domain)) return null

  const dormantRisk = DORMANT_RISK_DOMAINS.includes(domain)
  if (MAJOR_DOMAINS.includes(domain)) {
    return { domain, suggestion: null, suggestedEmail: null, dormantRisk }
  }

  let suggestion: string | null = TYPO_FIXES[domain] ?? null
  if (!suggestion) {
    for (const major of MAJOR_DOMAINS) {
      if (damerau(domain, major, 1) <= 1) { suggestion = major; break }
    }
  }
  return {
    domain,
    suggestion,
    suggestedEmail: suggestion ? `${local}@${suggestion}` : null,
    dormantRisk,
  }
}
